import React, { useEffect, useMemo, useRef, useState } from "react";
import { GeoJSON, MapContainer, TileLayer, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GeoArea } from "./services/listingsSearchCriteriaApi";
import { colorFromId, geoAreaContains } from "./mapUtils";
import { JourneyData } from "./services/tflApi";
import CommuteTooltip from "./CommuteTooltip";

export type Neighborhood = {
    id: string;
    name: string;
    geoArea: GeoArea;
};

type Props = {
    neighborhoods: Neighborhood[];
    initialCenter?: [number, number];
    initialZoom?: number;
    fitToPolygons?: boolean;
    selectedIds?: string[];
    onToggle?: (id: string) => void;
    journeyMap?: Map<string, JourneyData>;
};

function FitToFeatureCollection({ fc }: { fc: any }) {
    const map = useMap();
    useEffect(() => {
        if (!fc?.features?.length) return;
        const bounds = L.geoJSON(fc).getBounds();
        if (bounds.isValid()) map.fitBounds(bounds.pad(0.06));
    }, [fc, map]);
    return null;
}

function AttributionPrefix() {
    const map = useMap();
    useEffect(() => {
        map.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');
    }, [map]);
    return null;
}

function InvalidateSize() {
    const map = useMap();
    useEffect(() => {
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(map.getContainer());
        return () => observer.disconnect();
    }, [map]);
    return null;
}

function TouchHighlighter({ neighborhoods, onHit, onMiss, onToggle }: {
    neighborhoods: Neighborhood[];
    onHit: (id: string, name: string) => void;
    onMiss: () => void;
    onToggle?: (id: string) => void;
}) {
    const map = useMap();
    const ref = useRef({ neighborhoods, onHit, onMiss, onToggle });
    useEffect(() => { ref.current = { neighborhoods, onHit, onMiss, onToggle }; });

    useEffect(() => {
        const handler = (e: L.LeafletMouseEvent) => {
            const p: [number, number] = [e.latlng.lng, e.latlng.lat];
            for (const n of ref.current.neighborhoods) {
                if (geoAreaContains(n.geoArea, p)) {
                    ref.current.onHit(n.id, n.name);
                    ref.current.onToggle?.(n.id);
                    return;
                }
            }
            ref.current.onMiss();
        };
        map.on("click", handler);
        return () => { map.off("click", handler); };
    }, [map]);

    return null;
}

export default function ListingsMap({
    neighborhoods,
    initialCenter = [51.5074, -0.1278],
    initialZoom = 11,
    fitToPolygons = true,
    selectedIds = [],
    onToggle,
    journeyMap,
}: Props) {
    const isTouch = useMemo(() => window.matchMedia("(pointer: coarse)").matches, []);
    const [highlightedId, setHighlightedId] = useState<string | null>(null);
    const [label, setLabel] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<{ id: string; name: string; x: number; y: number } | null>(null);
    const [mobileTooltip, setMobileTooltip] = useState<{ id: string; name: string } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const showCommuteTooltip = !!journeyMap;

    useEffect(() => {
        setMobileTooltip(null);
    }, [selectedIds]);

    useEffect(() => {
        if (!label) return;
        const t = setTimeout(() => { setLabel(null); setHighlightedId(null); }, 2000);
        return () => clearTimeout(t);
    }, [label]);

    const fc = useMemo(() => {
        if (!neighborhoods?.length) return null;
        return {
            type: "FeatureCollection" as const,
            features: neighborhoods.map(n => ({
                type: "Feature" as const,
                properties: { id: n.id, name: n.name },
                geometry: n.geoArea as any,
            })),
        };
    }, [neighborhoods]);

    return (
        <div ref={containerRef} style={{ height: "100%", width: "100%", position: "relative" }}>
            {label && !showCommuteTooltip && (
                <div style={{
                    position: "absolute",
                    top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0,0,0,0.65)",
                    color: "#fff",
                    padding: "5px 14px",
                    borderRadius: 14,
                    fontSize: 13,
                    fontWeight: 500,
                    zIndex: 1000,
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                }}>
                    {label}
                </div>
            )}

            {showCommuteTooltip && !isTouch && tooltip && (
                <CommuteTooltip
                    areaName={tooltip.name}
                    journey={journeyMap.get(tooltip.id) ?? null}
                    visible
                    cursorX={tooltip.x}
                    cursorY={tooltip.y}
                />
            )}

            {showCommuteTooltip && isTouch && (
                <CommuteTooltip
                    areaName={mobileTooltip?.name ?? ''}
                    journey={mobileTooltip ? (journeyMap.get(mobileTooltip.id) ?? null) : null}
                    visible={!!mobileTooltip}
                    isBottomPanel
                    onClose={() => setMobileTooltip(null)}
                />
            )}

            <MapContainer
                center={initialCenter}
                zoom={initialZoom}
                minZoom={10}
                zoomControl={false}
                style={{ height: "100%", width: "100%" }}
            >
                <ZoomControl position="topleft" />
                <AttributionPrefix />
                <InvalidateSize />
                <TileLayer
                    url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />

                {isTouch && (
                    <TouchHighlighter
                        neighborhoods={neighborhoods}
                        onHit={(id, name) => {
                            if (showCommuteTooltip && !selectedIds.includes(id)) return;
                            setHighlightedId(id);
                            setLabel(name);
                            if (showCommuteTooltip) {
                                setMobileTooltip({ id, name });
                            }
                        }}
                        onMiss={() => setMobileTooltip(null)}
                        onToggle={onToggle}
                    />
                )}

                {fc && (
                    <>
                        {fitToPolygons && <FitToFeatureCollection fc={fc} />}
                        <GeoJSON
                            key={[...selectedIds, highlightedId ?? ""].join(",")}
                            data={fc as any}
                            style={(feature) => {
                                const id = feature?.properties?.id as string;
                                const selected = selectedIds.includes(id);
                                const highlighted = id === highlightedId;
                                const on = selected || highlighted;
                                return {
                                    fillColor: colorFromId(id),
                                    fillOpacity: on ? 0.60 : 0,
                                    color: on ? "#1a5276" : "transparent",
                                    opacity: on ? 1 : 0,
                                    weight: on ? 2.5 : 0,
                                };
                            }}
                            onEachFeature={(feature, layer) => {
                                const id = feature.properties?.id as string;
                                const name = feature.properties?.name as string;

                                if (isTouch) {
                                    layer.on("add", () => {
                                        const path = (layer as any)._path as SVGElement | undefined;
                                        if (path) path.style.pointerEvents = "none";
                                    });
                                    return;
                                }

                                if (!showCommuteTooltip && name) {
                                    (layer as any).bindTooltip(name, { sticky: true });
                                }

                                layer.on("mouseover", (e: L.LeafletMouseEvent) => {
                                    (layer as any).setStyle({
                                        fillColor: colorFromId(id),
                                        fillOpacity: 0.60,
                                        color: "#1a5276",
                                        weight: 2.5,
                                    });
                                    if (showCommuteTooltip && selectedIds.includes(id)) {
                                        const rect = containerRef.current?.getBoundingClientRect();
                                        setTooltip({
                                            id, name,
                                            x: rect ? e.originalEvent.clientX - rect.left : e.originalEvent.clientX,
                                            y: rect ? e.originalEvent.clientY - rect.top : e.originalEvent.clientY,
                                        });
                                    }
                                });

                                layer.on("mousemove", (e: L.LeafletMouseEvent) => {
                                    if (showCommuteTooltip && selectedIds.includes(id)) {
                                        const rect = containerRef.current?.getBoundingClientRect();
                                        setTooltip({
                                            id, name,
                                            x: rect ? e.originalEvent.clientX - rect.left : e.originalEvent.clientX,
                                            y: rect ? e.originalEvent.clientY - rect.top : e.originalEvent.clientY,
                                        });
                                    }
                                });

                                layer.on("mouseout", () => {
                                    const sel = selectedIds.includes(id);
                                    (layer as any).setStyle({
                                        fillOpacity: sel ? 0.60 : 0,
                                        color: sel ? "#1a5276" : "transparent",
                                        weight: sel ? 2.5 : 0,
                                    });
                                    setTooltip(null);
                                });

                                layer.on("click", () => onToggle?.(id));
                            }}
                        />
                    </>
                )}
            </MapContainer>
        </div>
    );
}
